/**
 * Captain Draft Node - Player View
 *
 * War-room draft board where captains build rosters pick-by-pick.
 * Active picking team pulses with energy; pick pool cards feel claimable.
 * Staggered animations as players slide into team rosters.
 * Uses reusable PlayerCard, TeamHeader, rank icons, and party colors.
 */

import type { QueueRankConfig } from "@rivaled/shared";
import { getMergedThresholds, getRankFromElo, getRankLevel, getTeamNames } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { RankIcon } from "../../../components/ui/RankIcon";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { PartyIcon } from "../../components/PartyIcon";
import { PlayerAvatar } from "../../components/PlayerAvatar";
import { PlayerCard } from "../../components/PlayerCard";
import { TeamHeader } from "../../components/TeamHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import { getPartyColor } from "../../lib/party-colors";
import { getTeamColor } from "../../lib/team-colors";
import type { NodePlayerViewProps } from "../types";

type CaptainDraftState = {
  captains: string[]; // index = team number (0-indexed)
  teams: Record<string, number>; // userId -> team number (0-indexed)
  pickPool: string[];
  currentPickingTeam: number; // 0-indexed
  pickIndex: number;
  totalPicks: number;
  phase: "captain_selection" | "drafting" | "complete";
  autoDraftedPlayers: string[];
};

export function CaptainDraftView({
  nodeState,
  players,
  context,
  onAction,
}: NodePlayerViewProps<CaptainDraftState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as CaptainDraftState;
  const config = nodeState.config as { labels?: { title?: string } };
  const title = config.labels?.title ?? "Captain Draft";
  const rankConfig = (context.rankConfig ?? null) as QueueRankConfig | null;

  const getPlayer = (id: string) => players.find((p) => p.userId === id);

  const teamCount = state.captains.length;

  // Seeded team names
  const teamNames = useMemo(
    () => getTeamNames(context.matchId, teamCount),
    [context.matchId, teamCount],
  );

  // Derive team panels from the teams record
  const teamEntries = useMemo((): [number, string[]][] => {
    const result: [number, string[]][] = [];
    for (let t = 0; t < teamCount; t++) {
      const members = Object.entries(state.teams)
        .filter(([, team]) => team === t)
        .map(([uid]) => uid);
      result.push([t, members]);
    }
    return result;
  }, [state.teams, teamCount]);

  // Captain lookup
  const getCaptain = (teamNum: number) => state.captains[teamNum];

  // Is the current user a captain?
  const isCaptain = state.captains.includes(userId ?? "");
  const currentCaptainId = getCaptain(state.currentPickingTeam);
  const isMyPick = currentCaptainId === userId;

  // Which team is the current user on?
  const myTeam = userId ? state.teams[userId] : undefined;

  // Is the current picking captain disconnected?
  const currentCaptain = getPlayer(currentCaptainId ?? "");
  const isCaptainDisconnected = state.phase === "drafting" && currentCaptain?.isConnected === false;

  // Party detection
  const myPartyId = getPlayer(userId ?? "")?.partyId ?? null;
  const autoDrafted = state.autoDraftedPlayers ?? [];

  // How many slots per team (total players / team count)
  const slotsPerTeam = Math.ceil(players.length / teamCount);

  // Team size for capacity checks
  const teamSize = slotsPerTeam;

  const handlePick = async (playerIds: string[]) => {
    if (!isMyPick || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    const result = await onAction("pick", { playerIds, captainId: userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  // Group pick pool into solo players and party groups
  type PoolEntry =
    | { type: "solo"; playerId: string }
    | { type: "party"; partyId: string; playerIds: string[] };

  const poolEntries = useMemo((): PoolEntry[] => {
    const partyGroups: Record<string, string[]> = {};
    const solos: string[] = [];

    for (const playerId of state.pickPool) {
      const player = getPlayer(playerId);
      if (player?.partyId) {
        if (!partyGroups[player.partyId]) partyGroups[player.partyId] = [];
        partyGroups[player.partyId].push(playerId);
      } else {
        solos.push(playerId);
      }
    }

    const eloOf = (id: string) => getPlayer(id)?.displayElo ?? 0;

    const entries: PoolEntry[] = [];

    // Party groups first (2+ members remaining = group card, 1 remaining = solo)
    const partyEntries: PoolEntry[] = [];
    for (const [partyId, ids] of Object.entries(partyGroups)) {
      if (ids.length >= 2) {
        // Sort members within party by ELO descending
        ids.sort((a, b) => eloOf(b) - eloOf(a));
        partyEntries.push({ type: "party", partyId, playerIds: ids });
      } else {
        solos.push(ids[0]);
      }
    }

    // Sort party groups by average ELO descending
    partyEntries.sort((a, b) => {
      if (a.type !== "party" || b.type !== "party") return 0;
      const avgA = a.playerIds.reduce((s, id) => s + eloOf(id), 0) / a.playerIds.length;
      const avgB = b.playerIds.reduce((s, id) => s + eloOf(id), 0) / b.playerIds.length;
      return avgB - avgA;
    });
    entries.push(...partyEntries);

    // Solo players sorted by ELO descending
    solos.sort((a, b) => eloOf(b) - eloOf(a));
    for (const playerId of solos) {
      entries.push({ type: "solo", playerId });
    }

    return entries;
  }, [state.pickPool, getPlayer]);

  const soloCount = poolEntries.filter((e) => e.type === "solo").length;

  // Capacity: current picking team's remaining slots
  const currentTeamMemberCount = useMemo(() => {
    return Object.values(state.teams).filter((t) => t === state.currentPickingTeam).length;
  }, [state.teams, state.currentPickingTeam]);
  const currentTeamRemainingSlots = teamSize - currentTeamMemberCount;

  // Picking team name for status text
  const pickingTeamName = teamNames[state.currentPickingTeam] ?? `Team ${state.currentPickingTeam}`;

  return (
    <NodeCard>
      <NodeHeader
        title={title}
        subtitle={
          <>
            <AnimatePresence mode="wait">
              <motion.p
                key={isMyPick ? "your-pick" : `team-${state.currentPickingTeam}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className="text-sm @sm:text-base"
              >
                {state.phase === "captain_selection" ? (
                  <span className="text-text-secondary">Selecting captains&hellip;</span>
                ) : isMyPick ? (
                  <span className="text-brand-400 font-semibold">Your turn to pick!</span>
                ) : (
                  <span className="text-text-secondary">
                    Waiting for {pickingTeamName} captain&hellip;
                  </span>
                )}
              </motion.p>
            </AnimatePresence>

            {/* Captain disconnected warning */}
            <AnimatePresence>
              {isCaptainDisconnected && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-error-500/10 border border-error-500/20">
                    <div className="w-1.5 h-1.5 rounded-full bg-error-400 animate-pulse" />
                    <span className="text-xs text-error-400">
                      Captain is offline — waiting for reconnection or timeout
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        }
      />

      {/* Progress pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <Users size={16} className="text-brand-400" aria-hidden="true" />
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.pickIndex}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.totalPicks}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            picked
          </span>
          <div className="w-px h-5 bg-surface-700/60" />
          <span className="font-display text-lg font-bold text-brand-400 tabular-nums">
            {state.pickPool.length}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            remain
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Team panels */}
      <div
        className={cn(
          "grid gap-4 @md:gap-5",
          teamCount <= 2
            ? "grid-cols-1 @md:grid-cols-2"
            : teamCount === 3
              ? "grid-cols-1 @lg:grid-cols-3"
              : "grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3",
        )}
      >
        {teamEntries.map(([teamNum, members], teamIndex) => {
          const captainId = getCaptain(teamNum);
          const isPicking = state.phase === "drafting" && teamNum === state.currentPickingTeam;
          const isMyTeam = myTeam === teamNum;
          const cardDelay = 0.1 + teamIndex * 0.08;
          const captain = getPlayer(captainId);
          const pickedMembers = members.filter((id) => id !== captainId);
          const emptySlots = Math.max(0, slotsPerTeam - 1 - pickedMembers.length);
          const teamName = teamNames[Number(teamNum)] ?? `Team ${teamNum}`;
          const teamColor = getTeamColor(teamNum, myTeam);

          return (
            <motion.div
              key={teamNum}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: cardDelay }}
              className={cn(
                "rounded-lg border overflow-hidden transition-shadow duration-500",
                isPicking
                  ? "bg-surface-800/60 border-brand-500/40 shadow-[0_0_20px_rgba(38,217,181,0.08)]"
                  : isMyTeam
                    ? "bg-surface-800/60 border-brand-500/20"
                    : cn("bg-surface-800/40", teamColor.border),
              )}
            >
              <TeamHeader
                teamName={teamName}
                isMyTeam={isMyTeam}
                isPicking={isPicking}
                rightContent={
                  isPicking ? (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-[10px] uppercase tracking-wider text-brand-400 font-semibold px-2 py-0.5 rounded bg-brand-500/15 border border-brand-500/20"
                    >
                      Picking
                    </motion.span>
                  ) : undefined
                }
              />

              <div className="p-2 @md:p-3 space-y-1.5">
                {/* Captain row */}
                {captain && (
                  <PlayerCard
                    player={captain}
                    variant="default"
                    isMe={captainId === userId}
                    partyColor={captainId === userId ? null : undefined}
                    rankConfig={rankConfig}
                    animationDelay={cardDelay + 0.15}
                    badges={
                      <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-amber-400/80 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/15">
                        CPT
                      </span>
                    }
                    className={cn(
                      captainId === userId ? "" : "bg-amber-500/[0.06] ring-1 ring-amber-500/15",
                    )}
                  />
                )}

                {/* Picked members */}
                <AnimatePresence>
                  {pickedMembers.map((playerId, playerIndex) => {
                    const player = getPlayer(playerId);
                    if (!player) return null;
                    const isMe = playerId === userId;
                    const isPartyMember =
                      !isMe && myPartyId !== null && player.partyId === myPartyId;
                    const isAutoDrafted = autoDrafted.includes(playerId);
                    const partyColor = getPartyColor(player.partyId, myPartyId);

                    return (
                      <PlayerCard
                        key={playerId}
                        player={player}
                        variant="compact"
                        isMe={isMe}
                        partyColor={partyColor}
                        rankConfig={rankConfig}
                        animationDelay={cardDelay + 0.25 + playerIndex * 0.06}
                        badges={
                          <>
                            {isPartyMember && (
                              <PartyIcon
                                size={12}
                                className={partyColor?.text ?? "text-amber-400/70"}
                              />
                            )}
                            {isAutoDrafted && (
                              <span className="shrink-0 text-[9px] uppercase tracking-wider font-semibold text-amber-400/70 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/10">
                                Party
                              </span>
                            )}
                          </>
                        }
                        className={
                          isAutoDrafted && !isMe
                            ? "bg-amber-500/[0.04] ring-1 ring-amber-500/10"
                            : undefined
                        }
                      />
                    );
                  })}
                </AnimatePresence>

                {/* Empty slots */}
                {emptySlots > 0 &&
                  Array.from({ length: emptySlots }).map((_, i) => (
                    <motion.div
                      key={`empty-${i}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: cardDelay + 0.3 + (pickedMembers.length + i) * 0.04 }}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-md border border-dashed border-surface-700/30"
                    >
                      <div className="shrink-0 w-7 h-7 rounded-full border border-dashed border-surface-700/40" />
                      <span className="text-xs text-surface-600 italic">Awaiting pick</span>
                    </motion.div>
                  ))}
              </div>

              {/* Team footer */}
              <div className="px-4 py-2 border-t border-surface-700/20 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-surface-500 font-medium">
                  {members.length} / {slotsPerTeam} player{slotsPerTeam !== 1 ? "s" : ""}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-surface-500 font-medium tabular-nums">
                  &Sigma; {members.reduce((sum, id) => sum + (getPlayer(id)?.displayElo ?? 0), 0)}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Pick pool */}
      {state.phase === "drafting" && state.pickPool.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mt-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-wider text-text-secondary">
              Available Players
            </span>
            <div className="flex-1 h-px bg-surface-700/40" />
            <span className="text-xs text-surface-500 tabular-nums">
              {state.pickPool.length} remaining
            </span>
          </div>

          {/* Party groups — full-width horizontal cards */}
          {poolEntries.some((e) => e.type === "party") && (
            <div className="space-y-3 mb-3">
              {poolEntries.map((entry, index) => {
                if (entry.type !== "party") return null;
                const partyTooLarge = entry.playerIds.length > currentTeamRemainingSlots;
                return (
                  <PartyPoolCard
                    key={entry.partyId}
                    playerIds={entry.playerIds}
                    index={index}
                    isMyPick={isMyPick}
                    disabled={isSubmitting || !isMyPick || partyTooLarge}
                    disabledReason={
                      partyTooLarge
                        ? `Need ${entry.playerIds.length} slots, ${currentTeamRemainingSlots} available`
                        : undefined
                    }
                    getPlayer={getPlayer}
                    onPick={handlePick}
                    rankConfig={rankConfig}
                    myPartyId={myPartyId}
                  />
                );
              })}
            </div>
          )}

          {/* Solo players — dense grid */}
          {poolEntries.some((e) => e.type === "solo") && (
            <div
              className={cn(
                "grid gap-3",
                soloCount <= 4
                  ? "grid-cols-1 @md:grid-cols-2"
                  : "grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3",
              )}
            >
              {poolEntries.map((entry, index) => {
                if (entry.type !== "solo") return null;
                const player = getPlayer(entry.playerId);
                if (!player) return null;
                const isMe = entry.playerId === userId;
                const partyColor = getPartyColor(player.partyId, myPartyId);
                const delay = Math.min(0.35 + index * 0.05, 0.7);

                return (
                  <PlayerCard
                    key={entry.playerId}
                    player={player}
                    variant="pool"
                    isMe={isMe}
                    partyColor={partyColor}
                    rankConfig={rankConfig}
                    animationDelay={delay}
                    animateFrom="bottom"
                    onClick={() => handlePick([entry.playerId])}
                    disabled={isSubmitting || !isMyPick}
                    badges={
                      !isMe && myPartyId !== null && player.partyId === myPartyId ? (
                        <PartyIcon size={12} className={partyColor?.text ?? "text-amber-400/70"} />
                      ) : null
                    }
                  />
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* Waiting message for non-captains in pick pool */}
      {state.phase === "drafting" && !isCaptain && state.pickPool.includes(userId ?? "") && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-6 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-800/60 border border-surface-700/40">
            <div className="w-2 h-2 rounded-full bg-amber-400/70 animate-pulse" />
            <span className="text-sm text-text-secondary">Waiting to be picked&hellip;</span>
          </div>
        </motion.div>
      )}

      {/* Draft complete indicator */}
      {state.phase === "drafting" && state.pickPool.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="mt-6 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
            <Check size={14} className="text-brand-400" aria-hidden="true" />
            <span className="text-sm text-text-secondary">
              Draft <span className="font-display font-semibold text-brand-400">complete</span>
            </span>
          </div>
        </motion.div>
      )}
    </NodeCard>
  );
}

// ---------------------------------------------------------------------------
// Party pool card - bundled group of party members
// ---------------------------------------------------------------------------

type PartyPoolCardProps = {
  playerIds: string[];
  index: number;
  isMyPick: boolean;
  disabled: boolean;
  disabledReason?: string;
  getPlayer: (id: string) =>
    | {
        userId: string;
        username: string;
        avatarUrl: string | null;
        displayElo: number;
        partyId: string | null;
      }
    | undefined;
  onPick: (playerIds: string[]) => void;
  rankConfig: QueueRankConfig | null;
  myPartyId: string | null;
};

function PartyPoolCard({
  playerIds,
  index,
  isMyPick,
  disabled,
  disabledReason,
  getPlayer,
  onPick,
  rankConfig,
  myPartyId: _myPartyId,
}: PartyPoolCardProps) {
  const delay = Math.min(0.35 + index * 0.05, 0.7);
  const totalElo = playerIds.reduce((sum, id) => sum + (getPlayer(id)?.displayElo ?? 0), 0);
  const avgElo = Math.round(totalElo / playerIds.length);
  const capacityBlocked = isMyPick && disabled && !!disabledReason;

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
      transition={{ duration: 0.3, delay }}
      whileHover={isMyPick && !disabled ? { scale: 1.01 } : undefined}
      whileTap={isMyPick && !disabled ? { scale: 0.99 } : undefined}
      onClick={() => onPick(playerIds)}
      disabled={disabled}
      className={cn(
        "w-full rounded-lg text-left transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-surface-900",
        capacityBlocked
          ? "bg-surface-800/30 border border-surface-700/20 cursor-not-allowed opacity-40"
          : isMyPick && !disabled
            ? "bg-surface-800/60 border border-amber-500/30 hover:border-amber-400/50 hover:bg-surface-800/80 hover:shadow-[0_0_16px_rgba(245,158,11,0.1)] cursor-pointer"
            : "bg-surface-800/40 border border-surface-700/30 cursor-not-allowed opacity-60",
      )}
    >
      {/* Top bar: party label + capacity info */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/10">
        <PartyIcon size={13} className="text-amber-400/80 shrink-0" />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-400/80">
          Party &middot; {playerIds.length} players
        </span>
        <div className="flex-1" />
        <span className="text-[10px] uppercase tracking-wider text-surface-500 font-medium tabular-nums">
          &Sigma; {totalElo} &middot; Avg {avgElo}
        </span>
        {capacityBlocked && (
          <span className="text-[9px] uppercase tracking-wider font-medium text-red-400/70 px-1.5 py-0.5 rounded bg-red-500/10">
            No room
          </span>
        )}
        {isMyPick && !disabled && (
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-400/70">
            Pick all &rarr;
          </span>
        )}
      </div>

      {/* Members row — inline chips, not nested cards */}
      <div className="flex items-center gap-3 px-3 py-2.5 flex-wrap">
        {playerIds.map((playerId) => {
          const player = getPlayer(playerId);
          if (!player) return null;

          // Rank resolution
          const thresholds = rankConfig ? getMergedThresholds(rankConfig) : null;
          const rank = thresholds ? getRankFromElo(player.displayElo, thresholds) : null;
          const level = rank && thresholds ? getRankLevel(player.displayElo, rank, thresholds) : 0;

          return (
            <div key={playerId} className="flex items-center gap-2">
              <PlayerAvatar
                avatarUrl={player.avatarUrl}
                username={player.username}
                size="md"
                ringColor="ring-amber-400/30 bg-surface-700 text-surface-400"
              />
              <span className="text-sm font-medium text-text-primary truncate max-w-[140px]">
                {player.username}
              </span>
              {rank && thresholds ? (
                <RankIcon rank={rank} level={level} size="sm" showLevels levelLayout="right" />
              ) : (
                <span className="text-xs tabular-nums text-surface-400 bg-surface-800/60 px-1.5 py-0.5 rounded">
                  {player.displayElo}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </motion.button>
  );
}
